import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Rundown, Story } from "../../common/types/domain";

interface StatsWidgetProps {
  stories: Story[];
  eventsCount: number;
  transcriptsCount: number;
}

export const StatsWidget: React.FC<StatsWidgetProps> = ({
  stories,
  eventsCount,
  transcriptsCount,
}) => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  // Calculate stats
  const totalLogs = stories.reduce(
    (acc, s) => acc + ((s as any).logCount || s.logs?.length || 0),
    0,
  );
  const totalStories = stories.length;
  const totalOtfs = stories.reduce(
    (acc, s) =>
      acc +
      (s.logs?.filter((l: any) => l.type === "otf" || l.role === "otf" || l.text?.includes("[OTF]"))
        .length || 0),
    0,
  );

  // Format large numbers
  const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

  const handleNavigate = (tab: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", tab);
        return params;
      },
      { replace: true },
    );
  };

  // Metric Item Component
  const Metric = ({
    label,
    value,
    icon,
    color = "#888",
    onClick,
  }: {
    label: string;
    value: number | string;
    icon: string;
    color?: string;
    onClick?: () => void;
  }) => (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "0 8px",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s",
      }}
      title={label}
      onClick={onClick}
      onMouseEnter={(e) => onClick && (e.currentTarget.style.transform = "scale(1.05)")}
      onMouseLeave={(e) => onClick && (e.currentTarget.style.transform = "scale(1)")}
    >
      <div style={{ fontSize: "0.9rem", fontWeight: "900", color: "#fff", lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "2px" }}>
        <i className={`bx ${icon}`} style={{ fontSize: "0.7rem", color }}></i>
        <span
          style={{
            fontSize: "0.55rem",
            color: "#666",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {label}
        </span>
      </div>
    </div>
  );

  const Sep = () => (
    <div
      style={{ width: "1px", height: "16px", background: "rgba(255,255,255,0.1)", margin: "0 4px" }}
    ></div>
  );

  return (
    <div
      data-testid="stats-widget"
      style={{
        display: "flex",
        alignItems: "center",
        padding: "2px 12px",
        background: "rgba(255, 255, 255, 0.02)",
        border: "1px solid rgba(255, 255, 255, 0.05)",
        borderRadius: "8px",
        marginRight: "12px",
      }}
    >
      <Metric
        label={t("controller.stats.otfs", "OTFS")}
        value={totalOtfs}
        icon="bx-conversation"
        color="var(--color-data-otf, #06b6d4)"
        onClick={() => handleNavigate("explore")}
      />
      <Sep />
      <Metric
        label={t("controller.stats.events")}
        value={eventsCount}
        icon="bx-calendar-event"
        color="var(--color-data-event, #4A9EFF)"
        onClick={() => handleNavigate("rundowns")}
      />
      <Sep />
      <Metric
        label={t("controller.stats.stories")}
        value={totalStories}
        icon="bx-purchase-tag"
        color="var(--color-data-story)"
        onClick={() => handleNavigate("explore")}
      />
      <Sep />
      <Metric
        label={t("controller.stats.logs")}
        value={fmt(totalLogs)}
        icon="bx-detail"
        color="var(--color-data-log)"
        onClick={() => handleNavigate("explore")}
      />
      <Sep />
      <Metric
        label={t("controller.stats.transcripts")}
        value={fmt(transcriptsCount)}
        icon="bx-microphone"
        color="var(--color-data-transcript, #A855F7)"
        onClick={() => handleNavigate("discovery")}
      />
    </div>
  );
};
